import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import api, { errMessage } from "@/lib/api";
import { ChangePassword } from "@/components/account/ChangePassword";
import { ActiveSessions } from "@/components/account/ActiveSessions";

export default function Profile() {
  const { user, refreshUser, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [institution, setInstitution] = useState("");
  const [saving, setSaving] = useState(false);
  const [sessionsKey, setSessionsKey] = useState(0);

  useEffect(() => {
    document.title = "Profile — CiteQR";
    if (user) { setName(user.name || ""); setInstitution(user.institution || ""); }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", { name, institution });
      await refreshUser();
      toast.success("Profile updated.");
    } catch (e) { toast.error(errMessage(e)); }
    finally { setSaving(false); }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/auth/account");
      setUser(false);
      toast.success("Your account and all projects were deleted.");
      navigate("/");
    } catch (e) { toast.error(errMessage(e)); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8 fade-in">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900">Profile</h1>
      <p className="mt-2 text-base text-slate-600">Manage your account and institution information.</p>

      <Card className="mt-8 border-slate-200 shadow-sm">
        <CardContent className="space-y-4 p-6">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="whitespace-nowrap text-sm text-slate-500">Signed in as</span>
            <span className="min-w-0 break-all text-sm font-medium text-slate-900" data-testid="profile-email">{user?.email}</span>
            <Badge variant="secondary" className="capitalize">{user?.auth_provider}</Badge>
            {user?.role === "admin" && <Badge className="bg-[#0A2540]">admin</Badge>}
          </div>
          <Separator />
          <div className="space-y-1.5">
            <Label htmlFor="p-name">Full name</Label>
            <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-inst">Institution</Label>
            <Input id="p-inst" value={institution} onChange={(e) => setInstitution(e.target.value)} data-testid="profile-institution" />
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="profile-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save changes
          </Button>
        </CardContent>
      </Card>

      {user?.auth_provider === "email" && <ChangePassword onChanged={() => setSessionsKey((k) => k + 1)} />}
      <ActiveSessions key={sessionsKey} />

      <Card className="mt-6 border-red-200 shadow-sm">
        <CardContent className="p-6">
          <h2 className="text-base font-semibold text-red-700">Delete account</h2>
          <p className="mt-1 text-sm text-slate-600">Permanently delete your account and all reference pages. This cannot be undone.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="mt-4 border-red-300 text-red-700 hover:bg-red-50" data-testid="delete-account-btn"><Trash2 className="mr-2 h-4 w-4" /> Delete my account</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                <AlertDialogDescription>This permanently removes your account and every reference page you created. This action is irreversible.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-account">Delete everything</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
